import React, { useEffect, useId, useRef, useState } from 'react';
import { cn } from '../../utils/cn';

export interface MenuItemDef {
  label: string;
  onSelect: () => void;
  icon?: React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
  /** Extra class on the rendered item button, e.g. for an E2E selector contract. */
  className?: string;
  /** Native title attribute on the rendered item button, e.g. for an E2E selector contract. */
  title?: string;
}

export interface MenuProps {
  trigger: (props: { onClick: () => void; 'aria-expanded': boolean; 'aria-haspopup': 'menu' }) => React.ReactNode;
  items: Array<MenuItemDef | 'separator'>;
  align?: 'start' | 'end';
  /**
   * Extra content rendered above the `role="menu"` list, e.g. a user summary or a theme
   * switch — but *outside* the menu element itself. `aria-required-children` only allows
   * menuitem/menuitemradio/menuitemcheckbox/group/separator as children of `role="menu"`,
   * so free text or a radiogroup must never be rendered inside it (see M11).
   */
  header?: React.ReactNode;
  className?: string;
}

export const Menu: React.FC<MenuProps> = ({ trigger, items, align = 'start', header, className }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDocClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Move focus onto the first menuitem as soon as the menu opens (standard menu-button pattern).
  useEffect(() => {
    if (!open) return;
    const first = menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)');
    first?.focus();
  }, [open]);

  const getMenuItems = () =>
    menuRef.current
      ? Array.from(menuRef.current.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)'))
      : [];

  const moveFocus = (delta: 1 | -1) => {
    const menuItems = getMenuItems();
    if (menuItems.length === 0) return;
    const currentIndex = menuItems.findIndex((el) => el === document.activeElement);
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + delta + menuItems.length) % menuItems.length;
    menuItems[nextIndex]?.focus();
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveFocus(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveFocus(-1);
    }
  };

  return (
    <div ref={containerRef} className={cn('relative inline-block', className)}>
      {trigger({ onClick: () => setOpen((o) => !o), 'aria-expanded': open, 'aria-haspopup': 'menu' })}
      {open && (
        <div
          className={cn(
            'absolute z-40 mt-2 min-w-[12rem] rounded-card border border-line bg-card p-1',
            align === 'end' ? 'right-0' : 'left-0'
          )}
        >
          {header && <div className="px-2 py-1.5">{header}</div>}
          <div role="menu" id={menuId} ref={menuRef} onKeyDown={handleMenuKeyDown}>
            {items.map((item, idx) =>
              item === 'separator' ? (
                <div key={`separator-${idx}`} role="separator" className="my-1 border-t border-line" />
              ) : (
                <button
                  key={item.label}
                  type="button"
                  role="menuitem"
                  title={item.title}
                  disabled={item.disabled}
                  onClick={() => {
                    setOpen(false);
                    item.onSelect();
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-control px-3 py-2 text-left text-sm hover:bg-sunken',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                    'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
                    item.danger ? 'text-danger-text' : 'text-ink',
                    item.className
                  )}
                >
                  {item.icon && <span aria-hidden="true">{item.icon}</span>}
                  {item.label}
                </button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};
